import React, { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import classnames from 'classnames';
import {
  Empty,
  Button,
  Checkbox,
  InputNumber,
  Row,
  Col,
  Typography,
} from 'antd';
import { CheckCircleOutlined } from '@ant-design/icons';

import {
  Form,
  StyledModal,
  WithLoading,
  FullWidthSpace,
} from 'common/components';
import {
  MinimumRequirementsRow,
  MinimumRequirementsText,
  MinimumRequirementsWrapperContent,
} from '../minimum-requirements';

import { mappingGridProperties } from 'pages/home/ribbon/components/sections/customize-grid/utils';

import { usePreviewRequirementForProduct } from 'hooks/usePreviewRequirementForProduct';

import { useGetProductFullView } from 'hooks';

import messages from 'i18n/messages/home';

import { MINIMUM_REQUIREMENT_ENTITY_TYPE } from 'pages/minimum-requirements/constants';

import './PreviewRequirementsModal.less';

const { Title } = Typography;

const PreviewRequirementsModalProduct = (props) => {
  const intl = useIntl();
  const [form] = Form.useForm();

  const [validationTriggered, setValidationTriggered] = useState(false);

  const { visible, setVisible, entity, entityType, otherPropsModal } = props;

  const {
    validateRequirements,
    additionalRequirements,
    validateLoading,
    fetchDataLoading,
    onSubmitRequiredPropertiesForm,
    submitLoading,
    submitDisabled,
    hasAdditionalRequirementsError,
    minimumRequirementErrorFieldNames,
    primaryErrors,
    allRequiredProperties,
  } = usePreviewRequirementForProduct({
    visible,
    closeModal: () => handleCancel(),
    entityId: entity?.id,
    entityType,
    form,
  });

  const isProductEntity =
    entityType?.toLowerCase() === MINIMUM_REQUIREMENT_ENTITY_TYPE.PRODUCT;

  const primaryErrorFieldNames = primaryErrors?.map((error) =>
    error?.fieldName?.toLowerCase()
  );
  const mappingMinimumRequirementErrorFieldNames =
    minimumRequirementErrorFieldNames.map((fieldName) =>
      fieldName?.toLowerCase()
    );

  const allErrorFieldNames = primaryErrorFieldNames.concat(
    mappingMinimumRequirementErrorFieldNames
  );

  const productId =
    hasAdditionalRequirementsError && isProductEntity ? entity?.id : null;
  const { productFull } = useGetProductFullView({ productId });

  const mappingAllProperties = useMemo(
    () => mappingGridProperties(allRequiredProperties),
    [allRequiredProperties]
  );

  const hasErrorFields =
    allErrorFieldNames?.length > 0 || hasAdditionalRequirementsError;

  const onClickValidateBtn = () => {
    setValidationTriggered(true);
    validateRequirements();
  };

  const onClickSubmitBtn = () => {
    onSubmitRequiredPropertiesForm();
  };

  const onClickCancelBtn = () => {
    handleCancel();
  };

  const handleCancel = () => {
    form && form.resetFields();
    setVisible(false);
  };

  //* RENDER
  const renderModalFooter = () => {
    const ValidateBtn = Button;
    const CancelBtn = Button;
    const SubmitBtn = Button;

    return (
      <Row>
        <Col flex={0}>
          <ValidateBtn
            type='primary'
            loading={validateLoading}
            onClick={onClickValidateBtn}
          >
            Check Validation
          </ValidateBtn>
        </Col>
        <Col flex={1}></Col>
        <Col flex={0}>
          <CancelBtn onClick={onClickCancelBtn}>
            {intl.formatMessage(messages.previewRequirementsCancelButton)}
          </CancelBtn>
          <SubmitBtn
            type='primary'
            onClick={onClickSubmitBtn}
            loading={submitLoading}
            disabled={submitDisabled}
          >
            Submit Changes
          </SubmitBtn>
        </Col>
      </Row>
    );
  };

  const renderEmpty = () => {
    return (
      <div className='requirement-preview-modal__empty-wrapper'>
        <Empty className='requirement-preview-modal__empty-icon' />
      </div>
    );
  };

  const renderSuccessMessage = () => {
    return (
      <div
        style={{
          textAlign: 'center',
          padding: '40px 20px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
        }}
      >
        <CheckCircleOutlined
          style={{ color: '#52c41a', fontSize: 40, margin: 8 }}
        />
        <Typography.Paragraph strong style={{ fontSize: 16, marginBottom: 0 }}>
          Product has passed all validation rules and is ready to be activated.
        </Typography.Paragraph>
        {validationTriggered && (
          <Typography.Paragraph strong style={{ fontSize: 16 }}>
            Please click Submit to save the data.
          </Typography.Paragraph>
        )}
      </div>
    );
  };

  const renderRequireProperties = (
    propertiesRequired,
    form,
    allErrorFieldNames
  ) => {
    const hasBrickCode = propertiesRequired.some((property) => {
      return property?.fieldName?.toLowerCase() === 'brickcode';
    });

    if (isProductEntity) {
      if (propertiesRequired?.length > 0) {
        return propertiesRequired.map((item) => {
          if (allErrorFieldNames.includes(item?.fieldName?.toLowerCase())) {
            return (
              <MinimumRequirementsRow
                cols={[12, 12]}
                key={item.id}
                status={item.checkStatus}
                errorComponent={item.errorComponent}
                item={item}
                entityData={entity?.data}
                updateFormValue={form?.setFieldsValue}
                hasBrickCode={hasBrickCode}
                primaryErrors={primaryErrors}
                minimumRequirementErrorFields={
                  minimumRequirementErrorFieldNames
                }
              >
                <MinimumRequirementsText
                  text={item.fullPathDisplayName}
                  isError={item.checkStatus === 'error'}
                  isWarning={item.checkStatus === 'warning'}
                />
              </MinimumRequirementsRow>
            );
          }
          return (
            <MinimumRequirementsRow
              cols={[12, 12]}
              key={item?.id}
              errorComponent={item.errorComponent}
              item={item}
              entityData={entity?.data}
              updateFormValue={form?.setFieldsValue}
              hasBrickCode={hasBrickCode}
              primaryErrors={primaryErrors}
              minimumRequirementErrorFields={minimumRequirementErrorFieldNames}
            >
              <MinimumRequirementsText
                text={item?.fullPathDisplayName}
                isError={false}
              />
            </MinimumRequirementsRow>
          );
        });
      }
    }

    if (propertiesRequired?.length > 0) {
      return propertiesRequired.map((item) => {
        return (
          <MinimumRequirementsRow
            cols={[12, 12]}
            key={item.id}
            status={item.checkStatus}
            errorComponent={item.errorComponent}
            item={item}
            entityData={entity?.data}
            updateFormValue={form?.setFieldsValue}
            hasBrickCode={hasBrickCode}
            primaryErrors={primaryErrors}
            minimumRequirementErrorFields={minimumRequirementErrorFieldNames}
          >
            <MinimumRequirementsText
              text={item.fullPathDisplayName}
              isError={item.checkStatus === 'error'}
            />
          </MinimumRequirementsRow>
        );
      });
    } else {
      return renderEmpty();
    }
  };

  const renderRequirePropertiesByModule = (mappingAllProperties) => {
    if (mappingAllProperties?.length > 0) {
      return mappingAllProperties.map((item) => {
        return (
          <MinimumRequirementsRow
            key={item.moduleDisplayName}
            cols={[24, 0]}
            showCheck={false}
          >
            <Row>
              <Col span={24}>
                <MinimumRequirementsText
                  className='minimum-requirement__subtitle'
                  text={item.moduleDisplayName}
                />
              </Col>
            </Row>
            {renderRequireProperties(
              item?.moduleProperties,
              form,
              allErrorFieldNames
            )}
          </MinimumRequirementsRow>
        );
      });
    } else {
      return renderEmpty();
    }
  };

  const modalProps = {
    visible,
    wrapClassName: 'requirement-preview-modal',
    title: entity
      ? intl.formatMessage(messages.previewRequirementsModalTitle[entity.type])
      : '',
    width: 800,
    bodyStyle: {
      position: 'relative',
      height: '80vh',
    },
    centered: true,
    closable: true,
    maskClosable: false,
    onCancel: handleCancel,
    footer: renderModalFooter(),
    ...otherPropsModal,
  };

  return (
    <StyledModal {...modalProps}>
      {fetchDataLoading ? (
        <WithLoading loading />
      ) : (
        <>
          {hasErrorFields ? (
            <>
              <FullWidthSpace size={20} id='validate-minimum-requirement'>
                <Title level={4} style={{ margin: 0 }}>
                  {entity?.gtin ? `GTIN ${entity?.gtin} - ` : ''}
                  {entity?.name ? entity?.name : ''}
                </Title>

                <Form form={form}>
                  <MinimumRequirementsWrapperContent title='Required Properties'>
                    {isProductEntity
                      ? renderRequirePropertiesByModule(mappingAllProperties)
                      : renderRequireProperties(mappingAllProperties, form)}
                  </MinimumRequirementsWrapperContent>

                  <MinimumRequirementsWrapperContent title='Additional Requirements'>
                    {additionalRequirements.length
                      ? additionalRequirements.map((item) => {
                          if (item.checkStatus === 'error') {
                            return (
                              <MinimumRequirementsRow
                                key={item.id}
                                cols={[12, 12]}
                                status={item.checkStatus}
                                errorComponent={item.errorComponent}
                                item={item}
                                entityData={entity?.data}
                                productDetail={productFull}
                              >
                                <Row gutter={[8, 0]}>
                                  <Col span={15}>
                                    <Checkbox
                                      checked={item.active}
                                      className='requirement-preview-modal__checkbox'
                                    >
                                      <MinimumRequirementsText
                                        text={item.name}
                                        isError={item.checkStatus === 'error'}
                                      />
                                    </Checkbox>
                                  </Col>
                                  <Col span={9}>
                                    {item?.hiddenTotal ? null : item?.value !==
                                      undefined ? (
                                      <InputNumber
                                        value={item.value}
                                        readOnly={true}
                                        defaultValue={0}
                                        className={classnames({
                                          'requirement-preview-modal__input': true,
                                          'requirement-preview-modal__input--error':
                                            item.checkStatus === 'error',
                                        })}
                                      />
                                    ) : null}
                                  </Col>
                                </Row>
                              </MinimumRequirementsRow>
                            );
                          }

                          return (
                            <MinimumRequirementsRow
                              key={item.id}
                              cols={[12, 12]}
                              status={item.checkStatus}
                              errorComponent={null}
                              item={{}}
                              entityData={{}}
                              productDetail={productFull}
                            >
                              <Row gutter={[8, 0]}>
                                <Col span={15}>
                                  <Checkbox
                                    checked
                                    className='requirement-preview-modal__checkbox'
                                  >
                                    <MinimumRequirementsText
                                      text={item.name}
                                      isError={false}
                                    />
                                  </Checkbox>
                                </Col>
                                <Col span={9}>
                                  {item?.hiddenTotal ? null : item?.value !==
                                    undefined ? (
                                    <InputNumber
                                      value={item.value}
                                      readOnly={true}
                                      defaultValue={0}
                                      className={classnames({
                                        'requirement-preview-modal__input': true,
                                      })}
                                    />
                                  ) : null}
                                </Col>
                              </Row>
                            </MinimumRequirementsRow>
                          );
                        })
                      : renderEmpty()}
                  </MinimumRequirementsWrapperContent>
                </Form>
              </FullWidthSpace>
            </>
          ) : (
            renderSuccessMessage()
          )}
        </>
      )}
    </StyledModal>
  );
};

export default PreviewRequirementsModalProduct;
